import { isAnimatedGifUrl } from './isAnimatedGifUrl';

const GIF_MAGIC = [0x47, 0x49, 0x46, 0x38]; // GIF8

function bytesLookLikeGif(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  return GIF_MAGIC.every((b, i) => bytes[i] === b);
}

function bytesLookLikeStaticImage(bytes: Uint8Array): boolean {
  // JPEG
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) return true;
  // PNG
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return true;
  }
  // WEBP (RIFF....WEBP)
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return true;
  }
  return false;
}

/**
 * Detect GIF without relying on a file extension (CDN URLs often have none).
 * Uses a simple CORS GET (no custom headers) so we avoid a Range preflight that
 * many CDNs reject — those failures used to leave Cards6 JPEGs stuck in fail-open.
 * @returns `true` / `false` when known, `null` when the probe fails (CORS/network).
 */
export async function sniffIsGifMedia(
  url: string | undefined | null
): Promise<boolean | null> {
  if (!url) return false;
  if (isAnimatedGifUrl(url)) return true;

  try {
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit'
    });
    if (!res.ok) return null;

    const contentType = res.headers.get('content-type') || '';
    if (/image\/gif/i.test(contentType)) return true;
    if (/image\/(jpeg|jpg|png|webp|svg\+xml)/i.test(contentType)) return false;

    // Only need the header for magic-byte checks.
    const buffer = await res.arrayBuffer();
    const bytes = new Uint8Array(buffer.slice(0, 16));
    if (bytesLookLikeGif(bytes)) return true;
    if (bytesLookLikeStaticImage(bytes)) return false;
    return null;
  } catch {
    return null;
  }
}
